import {tokens} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Card from '@/components/card'
import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import {ProofPointBadges} from '@/components/proof-point-badges/proof-point-badges'
import SEO from '@/components/seo'
import SimpleSignupForm from '@/components/simple-signup-form'
import type {SubscriptionsPageQuery} from '@/graphql-types'
import QuoteCarousel from '@/sections/quote-carousel'
import SignupBannerSection from '@/sections/signup-banner'
import TitleWithFeatures from '@/sections/title-with-features'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import TitleWithMediaItemsSection from '@/sections/title-with-media-items'
import {getVideoSources} from '@/utils/get-video-sources'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import {Section, BackgroundWrapper, Layout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import getEnvironmentFormId from '@helios/shared/utils/get-environment-field'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'

type Props = PageProps<SubscriptionsPageQuery>

const SubscriptionsPage = (props: Props) => {
    const data = props.data.allPrismicSubscriptionsPage.nodes[0].data

    if (!data) {
        return handleNoPrismicData()
    }

    const formId = getEnvironmentFormId(
        data.signup_banner.document.data.hubspot_form_id_production,
        data.signup_banner.document.data.hubspot_form_id_staging
    )

    return (
        <>
            <Header />

            <Main>
                <Wrapper>
                    <Section padding="small">
                        <FeatureBlock
                            image={data.hero[0].image}
                            title={data.hero[0].title}
                            body={data.hero[0].body.richText}
                            titleVariant="heading-base"
                        >
                            <SimpleSignupForm
                                {...data.signup_banner.document.data}
                                submitText={data.hero[0].submit_text}
                                withBorder
                                placeholder={
                                    data.signup_banner.document.data.email_input_placeholder
                                }
                                formId={formId}
                                errorText={data.signup_banner.document.data.email_input_error_text}
                            />

                            {!!data.hero[0].proof_point_badges?.document && (
                                <ProofPointBadges
                                    proofPoints={[
                                        data.hero[0].proof_point_badges.document.data
                                            .proof_point_first[0],
                                        data.hero[0].proof_point_badges.document.data
                                            .proof_point_second[0],
                                    ]}
                                />
                            )}
                        </FeatureBlock>
                    </Section>

                    <TitleWithIconGridSection
                        title={data.social_proof_logos_section.document.data.title}
                        items={data.social_proof_logos_section.document.data.logos.map(
                            ({logo}) => ({
                                icon: logo,
                            })
                        )}
                    />

                    {hasNonEmptyItems(data.steps) && (
                        <TitleWithFeatures title={data.steps_title} items={data.steps} />
                    )}

                    <TitleWithMediaItemsSection
                        title={data.how_it_works_title}
                        items={data.how_it_works_items}
                    />

                    {data.feature_blocks.map((block, i) => {
                        const video = getVideoSources({
                            webm: block.video_webm,
                            mp4: block.video_mp4,
                        })

                        return (
                            <Section key={`${block.title}-${i}`}>
                                <FeatureBlock
                                    image={block.image}
                                    title={block.title}
                                    body={block.body.richText}
                                    inlineVideo={video}
                                    mirrored={!(i % 2)}
                                    bodyProps={{
                                        variant: 'paragraph-base',
                                    }}
                                />
                            </Section>
                        )
                    })}

                    <TitleWithIconGridSection
                        titleVariant="heading-large"
                        highlightColor="yellow"
                        title={
                            data.social_proof_logos_extended_section.document.data.title.richText
                        }
                        items={data.social_proof_logos_extended_section.document.data.logos.map(
                            ({logo}) => ({
                                icon: logo,
                            })
                        )}
                    />

                    <TitleWithIconGridSection
                        titleVariant="heading-large"
                        highlightColor="pink"
                        title={data.social_proof_ratings_section.document.data.title.richText}
                        items={data.social_proof_ratings_section.document.data.ratings.map(
                            ({rating}) => ({
                                icon: rating.document.data.logo,
                                stars: rating.document.data.rating_number,
                                link: rating.document.data.link,
                            })
                        )}
                    />

                    <QuoteCarousel
                        items={data.customer_quote.map((quote) => ({
                            image: quote.image,
                            quote: quote.quote,
                            attribution: {
                                name: quote.attribution_name,
                                title: quote.attribution_title,
                            },
                        }))}
                    />

                    <BackgroundWrapper color="muted">
                        <Section>
                            <Layout variant="two-asymmetrical">
                                <LearnMoreTitle variant="heading-large">
                                    {data.learn_more_title}
                                </LearnMoreTitle>
                            </Layout>

                            <Layout variant="three-equal" alignItems="stretch">
                                {data.learn_more_cards.map(({learn_more_card}) => (
                                    <Card
                                        key={learn_more_card.document.id}
                                        {...learn_more_card.document.data}
                                        ctaText={learn_more_card.document.data.cta_text}
                                        ctaLink={learn_more_card.document.data.cta_link}
                                    />
                                ))}
                            </Layout>
                        </Section>
                    </BackgroundWrapper>

                    {data.signup_banner && (
                        <SignupBannerSection
                            {...data.signup_banner.document.data}
                            color="pink"
                            formId={formId}
                            placeholder={data.signup_banner.document.data.email_input_placeholder}
                            submitText={data.signup_banner.document.data.submit_text}
                            errorText={data.signup_banner.document.data.email_input_error_text}
                        />
                    )}
                </Wrapper>
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<SubscriptionsPageQuery>) => {
    const pageData = data.allPrismicSubscriptionsPage.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

const Wrapper = styled.div`
    overflow-x: hidden;
`

const LearnMoreTitle = styled(Text)`
    margin-bottom: ${tokens.spacing60};
`

export const query = graphql`
    query SubscriptionsPage($locale: String!) {
        allPrismicSubscriptionsPage(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    hero {
                        title
                        body {
                            richText
                        }
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        submit_text
                        proof_point_badges {
                            document {
                                ... on PrismicProofPointBadgesRepeatable {
                                    data {
                                        proof_point_first {
                                            title
                                            subtitle
                                            icon {
                                                alt
                                                copyright
                                                url
                                                dimensions {
                                                    height
                                                    width
                                                }
                                            }
                                        }
                                        proof_point_second {
                                            title
                                            subtitle
                                            icon {
                                                alt
                                                copyright
                                                url
                                                dimensions {
                                                    height
                                                    width
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    social_proof_logos_section {
                        document {
                            ... on PrismicSocialProofLogosSection {
                                data {
                                    title
                                    logos {
                                        logo {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    steps_title
                    steps {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                    }
                    how_it_works_title
                    how_it_works_items {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body
                    }
                    feature_blocks {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        video_mp4 {
                            ...Video
                        }
                        video_webm {
                            ...Video
                        }
                        title
                        body {
                            richText
                        }
                    }
                    social_proof_logos_extended_section {
                        document {
                            ... on PrismicSocialProofLogosExtendedSection {
                                data {
                                    title {
                                        richText
                                    }
                                    logos {
                                        logo {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    social_proof_ratings_section {
                        document {
                            ... on PrismicSocialProofRatingsSection {
                                data {
                                    title {
                                        richText
                                    }
                                    ratings {
                                        rating {
                                            document {
                                                ... on PrismicRating {
                                                    data {
                                                        logo {
                                                            alt
                                                            copyright
                                                            url
                                                            dimensions {
                                                                height
                                                                width
                                                            }
                                                        }
                                                        stars
                                                        rating_number
                                                        link
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    customer_quote {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        quote
                        attribution_name
                        attribution_title
                    }
                    learn_more_title
                    learn_more_cards {
                        learn_more_card {
                            document {
                                ... on PrismicLearnMoreCard {
                                    id
                                    data {
                                        image {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                        title
                                        body
                                        cta_text
                                        cta_link {
                                            ...PrismicLink
                                        }
                                    }
                                }
                            }
                        }
                    }
                    signup_banner {
                        document {
                            ...SignupBanner
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(SubscriptionsPage)
